import toString from "lodash/toString";
import { useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router";

type UseParamValueReturn<T> = {
  value: T;
  setValue: (value: T) => void;
  serialize: (value: T) => string;
};

type ParamValueMethods<T> = {
  parse: (rawValue: string | null) => T;
  format: (value: T) => string;
  isEmpty: (value: T) => boolean;
};

export const useParamValue = <T>(
  param: string,
  methods: Partial<ParamValueMethods<T>> & Pick<ParamValueMethods<T>, "parse">
): UseParamValueReturn<T> => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { parse, format, isEmpty } = useMemo<ParamValueMethods<T>>(
    () => ({
      format: toString,
      isEmpty: (value) => !value,
      ...methods
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(methods)]
  );

  const rawParamValue = searchParams.get(param);
  const value = useMemo(() => parse(rawParamValue), [parse, rawParamValue]);

  const setValue = useCallback(
    (value: T) => {
      if (isEmpty(value)) {
        searchParams.delete(param);
      } else {
        searchParams.set(param, format(value));
      }
      navigate({ search: searchParams.toString() }, { replace: true });
    },
    [isEmpty, navigate, searchParams, param, format]
  );

  const serialize = useCallback((value: T) => `${param}=${format(value)}`, [format, param]);

  return {
    value,
    setValue,
    serialize
  };
};

export const parseNumericParam = (value: string | null) => (value ? parseInt(value) : undefined);

export const useNumericParamValue = (param: string) =>
  useParamValue(param, { parse: parseNumericParam }).value;

export const useStringParamValue = (param: string) => useParamValue(param, { parse: toString }).value;
